import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut, Moon, AlertTriangle, ArrowRight, Bell, CalendarClock, HelpCircle } from "lucide-react";
import { SaldoCard, Panel, fmtH, fmtMin, fmtFecha, mesLabel } from "./portalShared";

export function MiJornadaCard({ estado }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!estado?.abierto) return undefined;
    const t = setInterval(() => setTick((x) => x + 1), 30000);
    return () => clearInterval(t);
  }, [estado?.abierto]);
  if (!estado) return null;
  const enCurso = estado.abierto && estado.entrada_abierta_desde
    ? Math.max(0, Math.floor((Date.now() - new Date(estado.entrada_abierta_desde)) / 60000)) : 0;
  const total = (estado.minutos_trabajados - (estado.minutos_en_curso || 0)) + enCurso;
  const sig = estado.siguiente_accion;
  return (
    <div className={`rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-4 ${
      estado.abierto ? "bg-emerald-500/5 border-emerald-500/30" : "bg-zinc-900/40 border-zinc-800/60"}`}
      data-testid="mi-jornada-card">
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> Mi jornada · hoy</div>
        <div className="text-2xl font-semibold mt-1 text-zinc-100" data-testid="mi-jornada-minutos">{fmtMin(total)}
          <span className="text-sm text-zinc-500 font-normal ml-2">trabajadas</span>
          {estado.abierto && <span className="ml-2 inline-flex items-center gap-1 text-emerald-300 text-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> en curso</span>}
        </div>
        {estado.abierto && estado.jornada_abierta_label && (
          <div className={`mt-2 inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${
            estado.jornada_desde_ayer ? "bg-indigo-500/10 border-indigo-500/30 text-indigo-200" : "bg-zinc-800/60 border-zinc-700 text-zinc-300"}`}
            data-testid="mi-jornada-label">
            {estado.jornada_desde_ayer && <Moon className="w-3 h-3" />} {estado.jornada_abierta_label}
          </div>
        )}
        {estado.entrada_huerfana && (
          <div className="mt-2 text-xs text-red-300 flex items-center gap-1" data-testid="mi-jornada-huerfana">
            <AlertTriangle className="w-3.5 h-3.5" /> Entrada del {estado.entrada_abierta_fecha} sin cerrar: solicita una corrección.
          </div>
        )}
        {!estado.puede_fichar && !estado.abierto && estado.motivo_no_puede === "sin_ruta_hoy" && (
          <div className="mt-2 text-xs text-zinc-400">Sin ruta asignada hoy: no procede fichar entrada.</div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {sig && (
          <Button asChild className={sig === "entrada" ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400" : "bg-amber-500 text-zinc-950 hover:bg-amber-400"}
            data-testid="mi-jornada-accion">
            <Link to="/fichaje">{sig === "entrada" ? <LogIn className="w-4 h-4 mr-2" /> : <LogOut className="w-4 h-4 mr-2" />}
              Fichar {sig}</Link>
          </Button>
        )}
        {!sig && <Button asChild variant="outline" className="bg-transparent border-zinc-700 text-zinc-300"><Link to="/fichaje">Ir a fichaje <ArrowRight className="w-3 h-3 ml-1" /></Link></Button>}
      </div>
    </div>
  );
}

export function KpisMes({ mes, saldo, onDesglose }) {
  if (!mes) return null;
  const b = mes.banco;
  const provisional = !b || b.estado !== "cerrado";
  return (
    <div data-testid="kpis-mes">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="text-xs uppercase tracking-wider text-zinc-500 capitalize">{mesLabel(mes.periodo)}
          <span className={`ml-2 text-[10px] px-2 py-0.5 rounded border normal-case ${provisional ? "bg-zinc-800 text-zinc-400 border-zinc-700" : "bg-emerald-500/10 text-emerald-300 border-emerald-500/30"}`}
            data-testid="kpis-estado-mes">{provisional ? "provisional · pendiente de cierre" : "mes cerrado"}</span>
        </div>
        {onDesglose && (
          <button onClick={onDesglose} className="text-xs text-amber-300 hover:text-amber-200 inline-flex items-center gap-1" data-testid="btn-por-que-saldo">
            <HelpCircle className="w-3.5 h-3.5" /> ¿Por qué tengo este saldo?
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <SaldoCard label="Debía trabajar" value={fmtH(mes.esperadas, false)} sub={`${mes.dias_laborables} días laborables`} testid="kpi-esperadas" />
        <SaldoCard label="Trabajadas" value={fmtH(mes.trabajadas, false)} sub={`${mes.dias_fichados} días fichados${mes.jornadas_nocturnas ? ` · ${mes.jornadas_nocturnas} 🌙` : ""}`} color="sky" testid="kpi-trabajadas" />
        <SaldoCard label="Saldo del mes" value={fmtH(mes.saldo)} color={mes.saldo > 0 ? "emerald" : mes.saldo < 0 ? "red" : "zinc"}
          sub={provisional ? "provisional" : "cerrado"} testid="kpi-saldo-mes" />
        {saldo && <>
          <SaldoCard label="Pendientes acumuladas" value={fmtH(saldo.pendiente_total)} color={saldo.pendiente_total > 0 ? "emerald" : saldo.pendiente_total < 0 ? "red" : "zinc"}
            sub={saldo.reservado_en_solicitudes ? `${saldo.disponible.toFixed(1)} h disponibles (−${saldo.reservado_en_solicitudes} h en solicitudes)` : "de periodos cerrados"} testid="kpi-pendiente" onClick={onDesglose} />
          <SaldoCard label="Compensadas" value={fmtH(b?.compensado || 0, false)} color="amber" sub="este mes" testid="kpi-compensado" />
          <SaldoCard label="Pagadas" value={fmtH(b?.pagado || 0, false)} color="violet" sub="este mes" testid="kpi-pagado" />
        </>}
      </div>
    </div>
  );
}

export function AvisosPanel({ avisos }) {
  if (!avisos?.length) return null;
  const cls = { warn: "border-amber-500/30 bg-amber-500/5 text-amber-200", error: "border-red-500/30 bg-red-500/5 text-red-200",
    info: "border-zinc-700 bg-zinc-900/40 text-zinc-300", ok: "border-emerald-500/30 bg-emerald-500/5 text-emerald-200" };
  return (
    <Panel title={`Avisos (${avisos.length})`} icon={Bell} testid="avisos-panel">
      <div className="p-3 space-y-2">
        {avisos.map((a, i) => (
          <div key={i} className={`text-xs rounded-lg border px-3 py-2 flex items-start gap-2 ${cls[a.nivel] || cls.info}`} data-testid={`aviso-${a.tipo}`}>
            <span className="flex-1">{a.texto}</span>
            {a.url && <Link to={a.url} className="underline shrink-0">ver</Link>}
          </div>
        ))}
      </div>
    </Panel>
  );
}

export function ProximosDias({ items }) {
  return (
    <Panel title="Próximos días" icon={CalendarClock} testid="proximos-panel">
      {!items?.length ? <div className="p-4 text-xs text-zinc-500">Nada previsto en los próximos 30 días.</div> : (
        <div className="divide-y divide-zinc-800/60">
          {items.map((p, i) => (
            <div key={i} className="px-4 py-2 flex items-center gap-3 text-sm" data-testid={`proximo-${p.tipo}`}>
              <div className="font-mono text-xs text-zinc-400 w-28 shrink-0">{fmtFecha(p.fecha)}{p.fecha_fin && p.fecha_fin !== p.fecha ? ` → ${fmtFecha(p.fecha_fin)}` : ""}</div>
              <div className="flex-1 min-w-0">
                <span className="text-zinc-100">{p.label}</span>
                {p.horas && <span className="text-indigo-200 text-xs ml-1">· {p.horas} h</span>}
                {p.detalle && <span className="text-zinc-500 text-xs ml-2 truncate">{p.detalle}</span>}
              </div>
              {p.url && <Link to={p.url} className="text-xs text-amber-300">abrir</Link>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
